// Głos bota w rozmowie. Wypowiedzi powstają na bieżąco (nie da się ich nagrać wcześniej w panelu), więc czyta je syntezator:
// Azure Neural (ten sam token co rozpoznawanie, niski koszt), a gdy zawiedzie — głos przeglądarki. Zawsze się kończy (nigdy nie rzuca).
// Za interfejsem `Speaker` można później podmienić głos na ElevenLabs bez zmian w ekranie rozmowy.
import { config } from '../config.ts';
import { stripTags } from '../lib/text.ts';
import { defaultSdkLoader } from './azure.ts';
import type { SdkLike, SdkLoader, TokenProvider } from './azure.ts';

export type SpeakLang = 'en' | 'pl';
export type VoiceUsed = 'azure' | 'browser' | 'none';

export interface Speaker {
  /** Czyta tekst i kończy się, gdy skończy mówić (albo gdy się nie udało). Zwraca, którym głosem zagrało. */
  speak(text: string, lang: SpeakLang): Promise<VoiceUsed>;
  stop(): void;
  /** Wywołaj w geście użytkownika (kliknięcie) — odblokowuje dźwięk w Safari/iOS. */
  unlock(): void;
}

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

const xmlEscape = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

/** SSML dla jednego zdania: wybrany głos Azure Neural i tempo. */
export function ssmlFor(text: string, lang: SpeakLang, voices: { en: string; pl: string } = config.chat.voices, rate: string = config.chat.speechRate): string {
  const tag = lang === 'pl' ? 'pl-PL' : 'en-US';
  return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${tag}"><voice name="${xmlEscape(voices[lang])}"><prosody rate="${xmlEscape(rate)}">${xmlEscape(text)}</prosody></voice></speak>`;
}

/** Najmniejszy kawałek <audio>, jakiego używamy (w testach atrapa). */
export interface AudioLike {
  src: string;
  onended: (() => void) | null;
  onerror: (() => void) | null;
  play(): Promise<void>;
  pause(): void;
}

export interface SpeakerOptions {
  tokens: TokenProvider;
  loader?: SdkLoader;
  voices?: { en: string; pl: string };
  rate?: string;
  /** Po ilu ms przerywamy syntezę (domyślnie 10 s). */
  synthTimeoutMs?: number;
  /** Fabryka elementu audio (w testach atrapa). */
  newAudio?: () => AudioLike | null;
  /** Blob → adres do odtworzenia (w testach atrapa). */
  objectUrl?: { create(data: ArrayBuffer): string; revoke(url: string): void };
  /** Głos zapasowy, gdy Azure zawiedzie (domyślnie głos przeglądarki). */
  browserSpeak?: (text: string, lang: SpeakLang, isCurrent: () => boolean) => Promise<void>;
}

/** Głos przeglądarki jako zapas: kończy się zawsze (zabezpieczenie czasowe, bo część przeglądarek nie wywołuje `onend`). */
export function speakWithBrowser(text: string, lang: SpeakLang, isCurrent: () => boolean = () => true): Promise<void> {
  return new Promise((resolve) => {
    const synth = typeof speechSynthesis !== 'undefined' ? speechSynthesis : null;
    if (!synth || !text || !isCurrent()) return resolve();
    let done = false;
    const end = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(end, Math.max(2500, text.length * 120));
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang === 'pl' ? 'pl-PL' : 'en-US';
      const voices = synth.getVoices();
      const voice = voices.find((v) => v.lang.replace('_', '-').toLowerCase() === u.lang.toLowerCase()) ?? voices.find((v) => v.lang.toLowerCase().startsWith(lang));
      if (voice) u.voice = voice;
      u.rate = 0.9;
      u.onend = end;
      u.onerror = end;
      synth.cancel();
      synth.speak(u);
    } catch {
      end();
    }
  });
}

export function createSpeaker(o: SpeakerOptions): Speaker {
  const loader = o.loader ?? defaultSdkLoader;
  const voices = o.voices ?? config.chat.voices;
  const rate = o.rate ?? config.chat.speechRate;
  const audio = o.newAudio ? o.newAudio() : typeof Audio !== 'undefined' ? (new Audio() as unknown as AudioLike) : null;
  const urls = o.objectUrl ?? { create: (d: ArrayBuffer) => URL.createObjectURL(new Blob([d], { type: 'audio/mpeg' })), revoke: (u: string) => URL.revokeObjectURL(u) };
  const fallback = o.browserSpeak ?? speakWithBrowser;
  let sdkPromise: Promise<SdkLike> | null = null;
  let token = 0;
  let cancel: (() => void) | null = null;

  /** Synteza Azure → mp3 w pamięci. Rzuca przy każdym problemie (wtedy gra głos przeglądarki). */
  async function synthesize(text: string, lang: SpeakLang): Promise<ArrayBuffer> {
    const { token: tk, region } = await o.tokens.get();
    sdkPromise ??= loader();
    let sdk: SdkLike;
    try {
      sdk = await sdkPromise;
    } catch (e) {
      sdkPromise = null;
      throw e;
    }
    if (!sdk.SpeechSynthesizer || !sdk.SpeechSynthesisOutputFormat) throw new Error('Ta wersja SDK nie ma syntezy mowy.');
    const speech = sdk.SpeechConfig.fromAuthorizationToken(tk, region);
    speech.speechSynthesisOutputFormat = sdk.SpeechSynthesisOutputFormat.Audio24Khz48KBitRateMonoMp3;
    const synth = new sdk.SpeechSynthesizer(speech, null);
    return new Promise<ArrayBuffer>((resolve, reject) => {
      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          synth.close();
        } catch {
          /* ignore */
        }
        fn();
      };
      const timer = setTimeout(() => finish(() => reject(new Error('Synteza Azure trwa za długo.'))), o.synthTimeoutMs ?? 10_000);
      try {
        synth.speakSsmlAsync(
          ssmlFor(text, lang, voices, rate),
          (r) => finish(() => (r.audioData && r.audioData.byteLength > 0 ? resolve(r.audioData) : reject(new Error(r.errorDetails || 'Azure nie zwrócił dźwięku.')))),
          (e) => finish(() => reject(new Error(e))),
        );
      } catch (e) {
        finish(() => reject(e instanceof Error ? e : new Error(String(e))));
      }
    });
  }

  function play(data: ArrayBuffer, mine: number): Promise<boolean> {
    return new Promise((resolve) => {
      if (!audio || mine !== token) return resolve(false);
      const url = urls.create(data);
      let done = false;
      const end = (ok: boolean) => {
        if (done) return;
        done = true;
        audio.onended = audio.onerror = null;
        urls.revoke(url);
        resolve(ok);
      };
      cancel = () => end(true); // przerwane przez stop() — nie przełączamy na głos zapasowy
      audio.onended = () => end(true);
      audio.onerror = () => end(false);
      audio.src = url;
      audio.play().catch(() => end(false));
    });
  }

  return {
    async speak(rawText, lang) {
      const text = stripTags(rawText);
      if (!text) return 'none';
      const mine = ++token;
      cancel?.();
      cancel = null;
      audio?.pause();
      try {
        const data = await synthesize(text, lang);
        if (mine !== token) return 'none';
        if (await play(data, mine)) return 'azure';
      } catch {
        /* przechodzimy na głos przeglądarki */
      }
      if (mine !== token) return 'none';
      await fallback(text, lang, () => mine === token);
      return 'browser';
    },
    stop() {
      token++;
      cancel?.();
      cancel = null;
      audio?.pause();
      if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    },
    unlock() {
      try {
        if (audio) {
          audio.src = SILENT_WAV;
          void audio.play().catch(() => {});
        }
      } catch {
        /* ignore */
      }
    },
  };
}
